import React from 'react';
import { Link } from 'react-router-dom';
import { Star, User, Briefcase, Monitor, MapPin } from 'lucide-react';
import { CurrencyIcon } from '../../utils/currencyIcon';

const getImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BASE_URL = API_URL.replace('/api', '');
  const path = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
  return `${BASE_URL}${path}`;
};

const formatAddress = (address) => {
  if (!address) return null;
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }
  return address;
};

const TherapistCard = ({ therapist, mcid, listView = false }) => {
  const {
    partner_id,
    name,
    qualification,
    work_experience,
    address,
    photo_url,
    fee_currency,
    video_sessions_enabled,
    fee_min,
    fee_max,
    avg_rating,
    review_count,
    specializations,
    bio
  } = therapist;

  const imageUrl = getImageUrl(photo_url);
  const formattedAddress = formatAddress(address);
  const profileUrl = mcid ? `/therapist/${partner_id}?mcid=${mcid}` : `/therapist/${partner_id}`;

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating - fullStars >= 0.5;
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
        );
      } else if (i === fullStars && hasHalf) {
        stars.push(
          <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400 opacity-60" />
        );
      } else {
        stars.push(
          <Star key={i} className="h-4 w-4 text-gray-300 dark:text-gray-600" />
        );
      }
    }
    return stars;
  };

  // List View Layout (similar to Practo)
  if (listView) {
    return (
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Profile Image */}
          <div className="w-full md:w-48 h-48 md:h-auto bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="h-20 w-20 text-gray-400 dark:text-dark-text-tertiary" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-5 md:p-6">
            <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
              {/* Left Content */}
              <div className="flex-1">
                {/* Name and Qualification */}
                <div className="mb-3">
                  <Link
                    to={profileUrl}
                    className="text-xl font-bold text-gray-900 dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-dark-primary-500 transition-colors inline-block"
                  >
                    {name}
                  </Link>
                  {qualification && (
                    <p className="text-sm text-teal-600 dark:text-teal-400 font-medium mt-1">
                      {qualification}
                    </p>
                  )}
                </div>

                {/* Experience */}
                {work_experience && (
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                    <Briefcase className="h-4 w-4 flex-shrink-0" />
                    <span>{work_experience}</span>
                  </div>
                )}

                {/* Specializations */}
                {specializations && specializations.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {specializations.slice(0, 3).map((spec, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full"
                        >
                          {spec}
                        </span>
                      ))}
                      {specializations.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full">
                          +{specializations.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Bio Preview */}
                {bio && (
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary line-clamp-2 mb-3">
                    {bio}
                  </p>
                )}

                {/* Session Mode and Location */}
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  {video_sessions_enabled && (
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <Monitor className="h-4 w-4 text-primary-600 dark:text-dark-primary-500" />
                      <span>Online</span>
                    </div>
                  )}
                  {address && (
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <MapPin className="h-4 w-4 text-primary-600 dark:text-dark-primary-500" />
                      <span>{formattedAddress || address}</span>
                    </div>
                  )}
                </div>

                {/* Reviews */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center">{renderStars(avg_rating)}</div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary">
                    {avg_rating > 0 ? avg_rating.toFixed(1) : '0.0'}
                  </span>
                  {review_count > 0 && (
                    <span className="text-sm text-gray-500 dark:text-dark-text-tertiary">
                      ({review_count} {review_count === 1 ? 'review' : 'reviews'})
                    </span>
                  )}
                </div>
              </div>

              {/* Right Content - Fee and CTA */}
              <div className="lg:w-48 flex flex-row lg:flex-col justify-between lg:justify-start items-start lg:items-end gap-4 lg:border-l lg:border-gray-200 dark:lg:border-dark-border lg:pl-6">
                {/* Fee */}
                {(fee_min || fee_max) && (
                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-dark-text-tertiary mb-1">
                      Consultation Fee
                    </div>
                    <div className="flex items-center gap-1 justify-end text-lg font-bold text-gray-900 dark:text-dark-text-primary">
                      <CurrencyIcon className="h-5 w-5 text-gray-700 dark:text-dark-text-secondary" />
                      <span>
                        {fee_min && fee_max && fee_min !== fee_max
                          ? `${fee_min} - ${fee_max}`
                          : fee_min || fee_max
                        }
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                      {fee_currency || 'INR'}
                    </div>
                  </div>
                )}

                {/* View Profile Button */}
                <Link
                  to={profileUrl}
                  className="w-full lg:w-auto px-6 py-2.5 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition text-center whitespace-nowrap"
                >
                  View Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original Grid View Layout
  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-200 dark:border-dark-border overflow-hidden flex flex-col">
      {/* Photo */}
      <div className="w-full h-48 bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover object-[center_30%]"
          />
        ) : (
          <User className="h-16 w-16 text-gray-400 dark:text-dark-text-tertiary" />
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Name */}
        <Link
          to={profileUrl}
          className="text-lg font-bold text-gray-900 dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-dark-primary-500 transition-colors"
        >
          {name}
        </Link>

        {/* Qualification */}
        {qualification && (
          <p className="text-sm text-teal-600 dark:text-teal-400 font-medium mt-1">
            {qualification}
          </p>
        )}

        {/* Experience */}
        {work_experience && (
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-dark-text-secondary">
            <Briefcase className="h-4 w-4 flex-shrink-0" />
            <span className="line-clamp-1">{work_experience}</span>
          </div>
        )}

        {/* Session Mode */}
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-dark-text-secondary">
          {video_sessions_enabled ? (
            <>
              <Monitor className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-dark-primary-500" />
              <span>Online</span>
            </>
          ) : (
            <>
              <MapPin className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-dark-primary-500" />
              <span>In-Person</span>
            </>
          )}
          {video_sessions_enabled && address && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <MapPin className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-dark-primary-500" />
              <span>In-Person</span>
            </>
          )}
        </div>

        {/* Address */}
        {formattedAddress && (
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-dark-text-tertiary">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="line-clamp-1">{formattedAddress}</span>
          </div>
        )}

        {/* Fee Range */}
        {(fee_min || fee_max) && (
          <div className="flex items-center gap-1 mt-2 text-sm font-semibold text-gray-800 dark:text-dark-text-primary">
            <CurrencyIcon className="h-4 w-4 text-gray-700 dark:text-dark-text-secondary" />
            <span>
              {fee_min && fee_max && fee_min !== fee_max
                ? `${fee_min} - ${fee_max}`
                : fee_min || fee_max
              }
            </span>
            <span className="text-xs font-normal text-gray-500 dark:text-dark-text-tertiary ml-1">
              {fee_currency || 'INR'}
            </span>
          </div>
        )}

        {/* Reviews */}
        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center">{renderStars(avg_rating)}</div>
          <span className="text-sm text-gray-500 dark:text-dark-text-tertiary">
            {avg_rating > 0 ? avg_rating.toFixed(1) : '0.0'}
            {review_count > 0 && ` (${review_count})`}
          </span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* View Profile Button */}
        <Link
          to={profileUrl}
          className="mt-4 block w-full text-center px-4 py-2 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition"
        >
          View Profile
        </Link>
      </div>
    </div>
  );
};

export default TherapistCard;
